<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { SIZES } from '@/utils/components-configurations/okr-icon'
import { OKR_LEVEL_ENTITY_KEYS } from '@/utils/entity-keys'
import { KR_VALUE_TYPES } from '@/utils/metrics-helpers'
import { CATEGORIZED_COLORS, ELEMENT_TYPES_CATEGORIES } from '@/utils/okr-levels'
import { usePlansLimitations } from '@/utils/web-app/plans-limitations'

import OkrIcon from '@/components/objectives/items/OkrIcon'
import AppTableCreateButton from '@/components/ui/AppTableCreateButton'
import AppTitle from '@/components/ui/AppTitle/AppTitle'

defineOptions({
  name: 'LevelsTemplates'
})

const props = defineProps({
  isLimitExceeded: {
    type: Boolean
  },

  createdLevels: {
    type: Array,
    default: () => []
  }
})

const { NAME, PREFIX, COLOR, TYPE_ID, FIELD_TYPE_IDS } = OKR_LEVEL_ENTITY_KEYS

const TYPE = 'type'
const TEMPLATES = [
  {
    [PREFIX]: 'I',
    [NAME]: 'Individual',
    [COLOR]: CATEGORIZED_COLORS.BLUE[2],
    [TYPE_ID]: ELEMENT_TYPES_CATEGORIES.OBJECTIVE.typeId,
    [TYPE]: ELEMENT_TYPES_CATEGORIES.OBJECTIVE.name
  },
  {
    [PREFIX]: 'In',
    [NAME]: 'Initiative',
    [COLOR]: CATEGORIZED_COLORS.GREEN[1],
    [FIELD_TYPE_IDS]: [KR_VALUE_TYPES.START_TARGET, KR_VALUE_TYPES.BINARY],
    [TYPE_ID]: ELEMENT_TYPES_CATEGORIES.METRIC.typeId,
    [TYPE]: ELEMENT_TYPES_CATEGORIES.METRIC.name
  },
  {
    [PREFIX]: 'O',
    [NAME]: 'Custom objective',
    [COLOR]: CATEGORIZED_COLORS.BLUE[1],
    [TYPE_ID]: ELEMENT_TYPES_CATEGORIES.OBJECTIVE.typeId,
    [TYPE]: ELEMENT_TYPES_CATEGORIES.OBJECTIVE.name,
    isCustom: true
  },
  {
    [PREFIX]: 'M',
    [NAME]: 'Custom metric',
    [COLOR]: CATEGORIZED_COLORS.VIOLET[1],
    [TYPE_ID]: ELEMENT_TYPES_CATEGORIES.METRIC.typeId,
    [TYPE]: ELEMENT_TYPES_CATEGORIES.METRIC.name,
    isCustom: true
  }
]

const { t } = useI18n()

/*const templatesToDisplay = computed(() => {
  return TEMPLATES.map(template => {
    const { color, prefix } = template
    return {
      ...template,
      levelColor: color.substring(1),
      levelPrefix: prefix
    }
  })
})*/

const createdLevelsNames = computed(() => {
  return props.createdLevels.map(level => level[NAME].toLowerCase().trim())
})

const templatesToDisplay = computed(() => {
  return TEMPLATES.reduce((acc, template) => {
    const { color, prefix, name, isCustom } = template

    if (!isCustom && createdLevelsNames.value.includes(name.toLowerCase())) {
      return acc
    }

    return [
      ...acc,
      {
        ...template,
        levelColor: color.substring(1),
        levelPrefix: prefix
      }
    ]
  }, [])
})

const emit = defineEmits({
  create: null
})

const onCreateTemplate = template => {
  emit('create', {
    ...template,
    name: template.isCustom ? '' : template[NAME]
  })
}

const { okrLevelsLimitations } = usePlansLimitations()
</script>

<template>
  <div class="lt-Wrapper">
    <AppTitle :level="5" disable-top-margin> {{ t('levels.templates.title') }}</AppTitle>
    <div class="lt-Description">{{ t('levels.templates.description') }}</div>

    <div class="lt-Templates">
      <div
        v-for="(template, index) in templatesToDisplay"
        :key="template[PREFIX]"
        class="lt-Template"
      >
        <div class="lt-Template_Header">
          <div class="lt-HeaderLeftPart">
            <OkrIcon
              :objective="template"
              :size="SIZES.LG"
              class="lt-Header_Icon"
              data-auto-testid="result-icon"
              data-testid="result-icon"
            />
            <div class="lt-Header_Name">
              {{ template[NAME] }}
            </div>
            <div class="lt-Header_Type">
              {{ t(template[TYPE]) }}
            </div>
          </div>
          <AppTableCreateButton
            v-tippy="{
              content: isLimitExceeded ? okrLevelsLimitations.getDisabledButtonTooltip() : null
            }"
            :disable="isLimitExceeded"
            class="lt-Header_CreateButton"
            icon-name="plus-small"
            no-hover
            no-padding
            @click="onCreateTemplate(template)"
          >
            {{ t('action.add') }}
          </AppTableCreateButton>
        </div>

        <div class="lt-Template_Description">
          {{ t(`levels_templates_description[${index}]`) }}
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.lt-Wrapper {
  margin-top: 44px;
}

.lt-Templates {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-top: 32px;
}

.lt-Template {
  --row-gap: 16px;
  padding: 20px 20px 24px 24px;
  border-radius: $border-radius-md-next;
  border: 1px solid $grey-2-next;
  display: grid;
  gap: var(--row-gap);
}

.lt-Template_Header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
}

.lt-HeaderLeftPart {
  gap: 2px var(--row-gap);
  display: grid;
  align-items: center;
  grid-template-columns: 1fr auto;
}

.lt-Header_Icon {
  grid-row: span 2;
}

.lt-Header_Name {
  font-size: $fs-14;
  line-height: 20px;
  color: $dark-1;
  font-weight: fw('semi-bold');
}

.lt-Header_Type {
  font-size: $fs-12;
  line-height: 20px;
  letter-spacing: -0.3px;
  color: $dark-3;
}

.lt-Template_Description {
  font-size: $fs-14;
  line-height: 20px;
  max-width: 412px;
  color: $dark-2;
}

.lt-Header_CreateButton {
  --gap: 0;
  font-weight: fw('semi-bold');
  // color: $primary-color-next;
}
</style>
